import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router";
import { AuthProvider } from "@/lib/auth-context";
import { ThemeProvider } from "@/lib/theme";
import { ThemeAccountSync } from "@/components/theme-account-sync";
import { WorkspaceLayout } from "@/layouts/workspace-layout";
import { WizardLayout } from "@/layouts/wizard-layout";
import { SuperAdminLayout } from "@/layouts/super-admin-layout";
import {
  RequireAuth,
  RequireMembership,
  RequireOrgAdmin,
  RequirePlatformAdmin,
} from "@/features/auth/guards";
import { LoginPage } from "@/features/auth/login-page";
import { SignupPage } from "@/features/auth/signup-page";
import { ForgotPasswordPage } from "@/features/auth/forgot-password-page";
import { ResetPasswordPage } from "@/features/auth/reset-password-page";
import { VerifyEmailPage } from "@/features/auth/verify-email-page";
import { OnboardingPage } from "@/features/onboarding/onboarding-page";
import { DashboardPage } from "@/features/dashboard/dashboard-page";
import { ConferencesPage } from "@/features/conferences/conferences-page";
import { ConferenceDetailPage } from "@/features/conferences/conference-detail-page";
import { StreamsPage } from "@/features/streams/streams-page";
import { UpcomingEventsPage } from "@/features/streams/upcoming-events-page";
import {
  IntegrationsPage,
  IntegrationsIndexRedirect,
  IntegrationsSourcesTab,
  IntegrationsDestinationsTab,
  IntegrationsAutoCreateTab,
  IntegrationsSetupLinksTab,
  IntegrationsWebhooksTab,
} from "@/features/integrations/integrations-page";
import { CredentialHandoffPage } from "@/features/integrations/credential-handoff-page";
import {
  SettingsPage,
  SettingsIndexRedirect,
  SettingsProfileTab,
  SettingsWorkspaceTab,
  SettingsWorkspaceIndex,
  SettingsBillingTab,
} from "@/features/settings/settings-page";
import { SettingsSecurityTab } from "@/features/settings/security-tab";
import {
  ReportsPage,
  ReportsIndexRedirect,
  ReportsActivityTab,
} from "@/features/reports/reports-page";
import {
  WizardIntroductionStep,
  WizardSelectStep,
  WizardDestinationsStep,
  WizardConfigureStep,
  WizardReviewStep,
} from "@/features/wizard/wizard-steps";
import {
  OrgAdminAuditPage,
  OrgAdminUsagePage,
  OrgAdminSettingsPage,
} from "@/features/org-admin/org-admin-pages";
import { SuperAdminDashboardPage } from "@/features/super-admin/dashboard-page";
import {
  SuperAdminUsersPage,
  SuperAdminUserDetailPage,
} from "@/features/super-admin/users-page";
import {
  SuperAdminAccountsPage,
  SuperAdminAccountDetailPage,
} from "@/features/super-admin/organizations-page";
import {
  SuperAdminStreamsPage,
  SuperAdminStreamDetailPage,
} from "@/features/super-admin/streams-page";
import {
  SuperAdminSubscriptionsPage,
  SuperAdminSubscriptionDetailPage,
} from "@/features/super-admin/subscriptions-page";
import { SuperAdminUsagePage } from "@/features/super-admin/usage-page";
import { SuperAdminAuditPage } from "@/features/super-admin/audit-page";
import {
  PlatformFailuresPage,
  PlatformJobsPage,
  PlatformFlagsPage,
  PlatformSettingsPage,
  PlatformIntegrationsPage,
  PlatformConferencesPage,
  PlatformOperationsPage,
  PlatformHealthPage,
} from "@/features/super-admin/ops-pages";
import { StyleGuidePage } from "@/features/super-admin/style-guide-page";
import {
  BillingReturnPage,
  InviteAcceptPage,
} from "@/features/billing/billing-return-page";
import { isWizardPath, wizardUnderlayLocation } from "@/lib/wizard-navigation";

function WizardRoutes() {
  return (
    <Routes>
      <Route element={<RequireAuth />}>
        <Route element={<RequireMembership />}>
          <Route path="/conferences/new" element={<WizardLayout />}>
            <Route index element={<Navigate to="introduction" replace />} />
            <Route path="introduction" element={<WizardIntroductionStep />} />
            <Route
              path="connect"
              element={<Navigate to="introduction" replace />}
            />
            <Route path="select" element={<WizardSelectStep />} />
            <Route path="destinations" element={<WizardDestinationsStep />} />
            <Route path="configure" element={<WizardConfigureStep />} />
            <Route path="review" element={<WizardReviewStep />} />
          </Route>
        </Route>
      </Route>
    </Routes>
  );
}

function RedirectToSettingsTab({ tab }: { tab: "billing" }) {
  return <Navigate to={`/settings/${tab}`} replace />;
}

function RedirectToReportsActivity() {
  return <Navigate to="/reports/activity" replace />;
}

/** Map legacy `/o/:orgId/...` bookmarks to session-scoped paths. */
function LegacyOrgRedirect() {
  const { "*": rest } = useParams();
  if (!rest) return <Navigate to="/" replace />;

  if (rest === "admin" || rest.startsWith("admin/")) {
    const adminRest =
      rest === "admin" || rest === "admin/" ? "" : rest.slice("admin/".length);
    return (
      <Navigate
        to={`/settings/workspace${adminRest ? `/${adminRest}` : ""}`}
        replace
      />
    );
  }
  if (rest === "billing") return <Navigate to="/settings/billing" replace />;
  if (rest === "activity") return <Navigate to="/reports/activity" replace />;
  return <Navigate to={`/${rest}`} replace />;
}

/** Map legacy `/platform/*` bookmarks to `/superadmin/*`. */
function LegacyPlatformRedirect() {
  const { "*": rest } = useParams();
  const location = useLocation();
  const suffix = rest ? `/${rest}` : "";
  return (
    <Navigate
      to={`/superadmin${suffix}${location.search}${location.hash}`}
      replace
    />
  );
}

/** Map legacy `/superadmin/organizations/:id` to `/superadmin/accounts/:id`. */
function LegacyOrganizationsRedirect() {
  const { accountId } = useParams();
  return <Navigate to={`/superadmin/accounts/${accountId ?? ""}`} replace />;
}

function AppRoutes() {
  const location = useLocation();
  const showWizard = isWizardPath(location.pathname);
  const underlayLocation = showWizard
    ? wizardUnderlayLocation(location)
    : location;

  return (
    <>
      <Routes location={underlayLocation}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route
          path="/billing/success"
          element={<BillingReturnPage kind="success" />}
        />
        <Route
          path="/billing/cancel"
          element={<BillingReturnPage kind="cancel" />}
        />
        <Route path="/invites/accept" element={<InviteAcceptPage />} />
        <Route
          path="/handoff/credentials"
          element={<CredentialHandoffPage />}
        />

        <Route element={<RequireAuth />}>
          <Route path="/onboarding" element={<OnboardingPage />} />

          <Route path="/o/:orgId" element={<Navigate to="/" replace />} />
          <Route path="/o/:orgId/*" element={<LegacyOrgRedirect />} />

          <Route
            path="/platform"
            element={<Navigate to="/superadmin" replace />}
          />
          <Route path="/platform/*" element={<LegacyPlatformRedirect />} />

          <Route element={<RequireMembership />}>
            <Route element={<WorkspaceLayout />}>
              <Route index element={<DashboardPage />} />
              <Route path="conferences" element={<ConferencesPage />} />
              <Route
                path="conferences/:conferenceId"
                element={<ConferenceDetailPage />}
              />
              <Route path="streams" element={<StreamsPage />} />
              <Route path="upcoming" element={<UpcomingEventsPage />} />
              <Route path="integrations" element={<IntegrationsPage />}>
                <Route index element={<IntegrationsIndexRedirect />} />
                <Route path="sources" element={<IntegrationsSourcesTab />} />
                <Route
                  path="destinations"
                  element={<IntegrationsDestinationsTab />}
                />
                <Route
                  path="auto-create"
                  element={<IntegrationsAutoCreateTab />}
                />
                <Route
                  path="setup-links"
                  element={<IntegrationsSetupLinksTab />}
                />
                <Route path="webhooks" element={<IntegrationsWebhooksTab />} />
              </Route>
              <Route path="reports" element={<ReportsPage />}>
                <Route index element={<ReportsIndexRedirect />} />
                <Route path="activity" element={<ReportsActivityTab />} />
              </Route>
              <Route path="activity" element={<RedirectToReportsActivity />} />
              <Route
                path="billing"
                element={<RedirectToSettingsTab tab="billing" />}
              />
              <Route path="settings" element={<SettingsPage />}>
                <Route index element={<SettingsIndexRedirect />} />
                <Route path="profile" element={<SettingsProfileTab />} />
                <Route path="security" element={<SettingsSecurityTab />} />
                <Route
                  path="organization"
                  element={<Navigate to="/settings/workspace" replace />}
                />
                <Route
                  path="organization/*"
                  element={<Navigate to="/settings/workspace" replace />}
                />
                <Route path="workspace" element={<SettingsWorkspaceTab />}>
                  <Route index element={<SettingsWorkspaceIndex />} />
                  <Route element={<RequireOrgAdmin />}>
                    <Route path="usage" element={<OrgAdminUsagePage />} />
                    <Route path="audit" element={<OrgAdminAuditPage />} />
                    <Route path="settings" element={<OrgAdminSettingsPage />} />
                  </Route>
                </Route>
                <Route path="billing" element={<SettingsBillingTab />} />
                <Route
                  path="activity"
                  element={<RedirectToReportsActivity />}
                />
              </Route>
            </Route>
          </Route>

          <Route element={<RequirePlatformAdmin />}>
            <Route path="/superadmin" element={<SuperAdminLayout />}>
              <Route index element={<SuperAdminDashboardPage />} />
              <Route path="users" element={<SuperAdminUsersPage />} />
              <Route
                path="users/:userId"
                element={<SuperAdminUserDetailPage />}
              />
              <Route path="accounts" element={<SuperAdminAccountsPage />} />
              <Route
                path="accounts/:accountId"
                element={<SuperAdminAccountDetailPage />}
              />
              <Route
                path="organizations"
                element={<Navigate to="/superadmin/accounts" replace />}
              />
              <Route
                path="organizations/:accountId"
                element={<LegacyOrganizationsRedirect />}
              />
              <Route path="streams" element={<SuperAdminStreamsPage />} />
              <Route
                path="streams/:streamId"
                element={<SuperAdminStreamDetailPage />}
              />
              <Route
                path="subscriptions"
                element={<SuperAdminSubscriptionsPage />}
              />
              <Route
                path="subscriptions/:accountId"
                element={<SuperAdminSubscriptionDetailPage />}
              />
              <Route
                path="billing"
                element={<Navigate to="/superadmin/subscriptions" replace />}
              />
              <Route path="usage" element={<SuperAdminUsagePage />} />
              <Route path="audit" element={<SuperAdminAuditPage />} />
              <Route path="conferences" element={<PlatformConferencesPage />} />
              <Route path="operations" element={<PlatformOperationsPage />} />
              <Route path="failures" element={<PlatformFailuresPage />} />
              <Route
                path="integrations"
                element={<PlatformIntegrationsPage />}
              />
              <Route path="jobs" element={<PlatformJobsPage />} />
              <Route path="health" element={<PlatformHealthPage />} />
              <Route path="flags" element={<PlatformFlagsPage />} />
              <Route path="settings" element={<PlatformSettingsPage />} />
              <Route path="style-guide" element={<StyleGuidePage />} />
            </Route>
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {showWizard ? <WizardRoutes /> : null}
    </>
  );
}

export function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ThemeAccountSync />
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
